<template>
  <div v-redirect-if-no-permission:ACCOUNTING :key="switch1">
    <loading-component :value="loading" />

    <div>
      <div>
        <v-toolbar flat color="white">
          <v-toolbar-title>{{ $t("accountingPage.transferCentersTransfersTitle") }}</v-toolbar-title>
          <v-spacer></v-spacer>

          <v-text-field v-model="search" style="width: 20%;" append-icon="search" :label='$t("accountingPage.searchNameOrAccount")' single-line
            hide-details @keyup.enter="getAccountingData(false)" @click:append="getAccountingData(false)"></v-text-field>

          <choose-date :label='$t("accountingPage.from")' style="margin-top: 20px;width: 20%; margin-inline-start: 10px;"
            v-model="fromDate"></choose-date>
          <choose-date :label='$t("accountingPage.to")' style="margin-top: 20px;width: 20%; margin-inline-start: 10px;"
            v-model="toDate"></choose-date>
          <v-btn color="primary" dark class="mb-2 cbtn" @click="getAccountingData(false)" :loading="loading">{{
            $t("accountingPage.getData") }}</v-btn>
          <v-btn color="primary" dark class="mb-2 cbtn" @click="getAccountingData(true)" :loading="loading">{{
            $t("accountingPage.exportCsv") }}</v-btn>
        </v-toolbar>
      </div>

      <v-data-table :headers="headers" :items="accountingData" class="elevation-1" :pagination.sync="pagination"
        :rows-per-page-items="[5, 10, 15, 25, 100]" :loading="loading" :total-items="totalItems">
        <template v-slot:items="props">
          <td class="text-xs-start">{{ props.item.id }}</td>
          <td class="text-xs-start">{{ props.item.transfer_center_name }}</td>
          <td class="text-xs-start">{{ props.item.transfer_center_phone_number }}</td>
          <td class="text-xs-start">{{ props.item.center_account_code || '-' }}</td>
          <td class="text-xs-start">{{ props.item.user_full_name }}</td>
          <td class="text-xs-start">{{ props.item.user_email }}</td>
          <td class="text-xs-start">{{ props.item.amount | money }}</td>
          <td class="text-xs-start">{{ formatDate(props.item.created_at) }}</td>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import DataTable from "@/components/monaco/DataTable";
import { companyNow, companyToday, formatCompanyTime } from "@/util/companyTime";
import LoadingComponent from "@/components/LoadingComponent";
import ChooseDate from "@/components/monaco/input/ChooseDate";

export default {
  name: "TransferCenterTransfers",
  title() {
    return this.$t("accountingPage.transferCentersTransfersTitle");
  },
  components: {
    LoadingComponent,
    DataTable,
    ChooseDate
  },
  data() {
    return {
      rules: {
        length: (len) => (v) =>
          (v || "").length >= len || this.$t("accountingPage.invalidLength", { len }),
        required: (v) => !!v || this.$t("accountingPage.requiredField"),
        number: (v) => /^\d+(\.\d+)?$/.test(v) || this.$t("accountingPage.positiveNumber")
      },
      accountingData: [],
      loading: false,
      search: '',
      fromDate: companyNow().startOf("year").format("YYYY-MM-DD"),
      toDate: companyToday(),
      totalItems: 0,
      pagination: {
        sortBy: "id",
        descending: true,
        rowsPerPage: 25,
        page: 1,
      },
      switch1: null,
      isInitialLoad: true,
    };
  },
  computed: {
    headers() {
      return [
        { text: "#", value: "id" },
        { text: this.$t("accountingPage.transferCenterName"), value: "transfer_center_name" },
        { text: this.$t("accountingPage.transferCenterPhone"), value: "transfer_center_phone_number" },
        { text: this.$t("accountingPage.centerAccountCode"), value: "center_account_code", sortable: false },
        { text: this.$t("accountingPage.userName"), value: "user_full_name" },
        { text: this.$t("accountingPage.userEmail"), value: "user_email" },
        { text: this.$t("accountingPage.amount"), value: "amount" },
        { text: this.$t("accountingPage.date"), value: "created_at" }
      ];
    },
  },
  mounted() {
    this.getAccountingData();
    this.$nextTick(() => {
      this.isInitialLoad = false;
    });
  },
  watch: {
    pagination: {
      handler() {
        if (!this.isInitialLoad) {
          this.getAccountingData();
        }
      },
      deep: true,
    },
  },
  methods: {
    formatDate(date) {
      // Final-version doc 2026-10-05: this endpoint sends ISO "Z" values, so
      // print them on the company clock (UTC+3), not the PC's zone.
      return formatCompanyTime(date, "DD-MM-YYYY HH:mm:ss", "");
    },
    formatType(value) {
      if (!value) return "";
      let tmp = value.split("\\");
      return tmp[tmp.length - 1];
    },
    getAccountingData(excel = false) {
      // Accounting doc 2026-09-07 (item 3.2-4): the CSV downloads only when
      // the export button asks for it. Anything else (a search, a page
      // change, a sort) is a normal fetch, whatever it passed in.
      excel = excel === true;
      const params = new URLSearchParams();
      const sortBy = this.pagination.sortBy || "id";
      const descending = Boolean(this.pagination.descending);

      if (!this.fromDate || !this.toDate) {
        window.getApp.$emit("SHOW_TOAST", this.$t("accountingPage.selectDateRange"), "red");
        return;
      }
      if (this.fromDate > this.toDate) {
        window.getApp.$emit("SHOW_TOAST", this.$t("accountingPage.fromBeforeTo"), "red");
        return;
      }
      this.loading = true;
      params.append("perPage", String(this.pagination.rowsPerPage));
      params.append("page", this.pagination.page);
      params.append("sortBy", sortBy);
      params.append("descending", String(descending));
      params.append("excel", excel ? 1 : 0);
      params.append("from", this.fromDate);
      params.append("to", this.toDate);
      if (this.search) {
        params.append("search", this.search);
      }

      this.axios
        .get("transfer-center-transfers", { params: params, responseType: excel ? "blob" : "json" })
        .then(({ data }) => {
          if (!excel) {
            const responseData = data.data || {};
            this.accountingData = responseData.data || [];
            this.totalItems = responseData.total || 0;
            if (responseData.per_page) {
              this.pagination.rowsPerPage = Number(responseData.per_page);
            }
          } else {
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "transfer_center_transfers.csv");
            document.body.appendChild(link);
            link.click();
          }
        })
        .catch(() => {
          this.accountingData = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.filters-container {
  padding: 16px;
}

.cards-container {
  padding: 16px;
}

.payment-card {
  width: 100%;
}

.card-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.card-row:last-child {
  border-bottom: none;
}

.card-label {
  font-weight: 600;
  color: #666;
  font-size: 14px;
}

.card-value {
  color: #333;
  font-size: 14px;
  text-align: end;
}

.card-actions {
  padding: 8px 16px;
  justify-content: flex-end;
}

.pagination-container {
  padding: 16px;
  display: flex;
  justify-content: center;
}

.date-filter {
  max-width: 170px;
  margin-inline-end: 12px;
}
</style>
